// src/components/SharePointSearch/SharePointSearchSection.tsx
// =================================================================
// SHAREPOINT SEARCH SECTION - INLINE SEARCH/READ RESULTS
// =================================================================
//
// A collapsible disclosure that appears inline within AI chat responses
// when the AI invokes the sharepoint_search tool. Handles search results,
// document reads, no-results, and error states.
// Follows the CatylexSearchSection pattern.
// =================================================================

import * as React from "react";
import { useState, useCallback, useEffect, useRef } from "react";
import {
  NormalizedSharePointResult,
  NormalizedSharePointSearch,
  NormalizedSharePointRead,
  NormalizedSharePointError,
} from "../../interfaces/sharepointSearchTypes";
import { createLogger } from "../../utils/logger";
import SharePointFileList from "./SharePointFileList";
import SharePointDocumentCard from "./SharePointDocumentCard";
import SharePointNoResults from "./SharePointNoResults";
import SharePointError from "./SharePointError";
import { SharePointLogoIcon, ChevronIcon } from "./SharePointIcons";
import {
  spColors,
  containerStyle,
  createHeaderStyle,
  textContentStyle,
  headerTextStyle,
  separatorStyle,
  summaryTextStyle,
  createCollapseWrapperStyle,
  createExpandedContentStyle,
  contentAreaStyle,
} from "./sharepointSearchStyles";

const log = createLogger("SharePointSearchSection");

interface SharePointSearchSectionProps {
  result: NormalizedSharePointResult;
}

const SharePointSearchSection: React.FC<SharePointSearchSectionProps> = ({ result }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    log.debug("SharePointSearchSection mounted", {
      status: result.status,
      action: result.action,
    });
  }, [result]);

  const handleToggle = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handleToggle();
      }
    },
    [handleToggle]
  );

  const handleMouseEnter = useCallback(() => {
    if (headerRef.current) headerRef.current.style.backgroundColor = spColors.mutedBgHover;
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (headerRef.current) headerRef.current.style.backgroundColor = spColors.mutedBg;
  }, []);

  const getHeaderLabel = (): string => {
    if (result.status === "error") return "SharePoint";
    if (result.action === "read") return "Document fetched";
    return "SharePoint Search";
  };

  const getHeaderSummary = (): string => {
    if (result.status === "error") {
      const err = result as NormalizedSharePointError;
      return err.action === "read" ? "Failed to fetch document" : "Search failed";
    }

    if (result.action === "search") {
      const search = result as NormalizedSharePointSearch;
      if (search.files.length === 0) return "No results";
      return `${search.totalResults} document${search.totalResults !== 1 ? "s" : ""} found`;
    }

    if (result.action === "read") {
      const read = result as NormalizedSharePointRead;
      return read.fileName;
    }

    return "";
  };

  const getIconBg = (): string => {
    if (result.status === "error") return spColors.errorBg;
    if (result.action === "search" && (result as NormalizedSharePointSearch).files?.length === 0) {
      return spColors.warningBg;
    }
    return spColors.tealBg;
  };

  const getLabelColor = (): string => {
    if (result.status === "error") return spColors.errorColor;
    return spColors.foreground;
  };

  const renderExpandedContent = () => {
    if (result.status === "error") {
      return <SharePointError result={result as NormalizedSharePointError} />;
    }

    if (result.action === "search") {
      const search = result as NormalizedSharePointSearch;
      if (search.files.length === 0) {
        return <SharePointNoResults result={search} />;
      }
      return (
        <SharePointFileList
          files={search.files}
          totalResults={search.totalResults}
          moreResultsAvailable={search.moreResultsAvailable}
        />
      );
    }

    if (result.action === "read") {
      return <SharePointDocumentCard result={result as NormalizedSharePointRead} />;
    }

    return null;
  };

  const iconContainerStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "24px",
    height: "24px",
    borderRadius: "6px",
    backgroundColor: getIconBg(),
    flexShrink: 0,
  };

  return (
    <div style={containerStyle}>
      <div
        ref={headerRef}
        style={createHeaderStyle(isExpanded)}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
        aria-label={`${isExpanded ? "Collapse" : "Expand"} SharePoint search results`}
      >
        <div style={iconContainerStyle}>
          <SharePointLogoIcon />
        </div>

        <div style={textContentStyle}>
          <p style={headerTextStyle}>
            <span style={{ color: getLabelColor(), fontWeight: 500 }}>{getHeaderLabel()}</span>
            <span style={separatorStyle}>&middot;</span>
            <span style={summaryTextStyle}>{getHeaderSummary()}</span>
          </p>
        </div>

        <ChevronIcon isExpanded={isExpanded} />
      </div>

      <div
        style={createCollapseWrapperStyle(isExpanded)}
        aria-hidden={!isExpanded}
        // `inert` keeps focus and AT out of the collapsed panel; cast because
        // React's CSSProperties doesn't yet model the HTML attribute.
        {...({ inert: !isExpanded ? "" : undefined } as Record<string, string | undefined>)}
      >
        <div style={createExpandedContentStyle(isExpanded)}>
          {result.action === "search" && result.status === "success" && (
            <div
              style={{
                padding: "10px 16px",
                backgroundColor: spColors.mutedBg,
                display: "flex",
                alignItems: "center",
                gap: "12px",
                fontSize: "12px",
                color: spColors.mutedForeground,
              }}
            >
              <SharePointLogoIcon size={20} />
              <span>
                <span style={{ fontWeight: 600, color: spColors.headerText }}>Searched for:</span>{" "}
                {(result as NormalizedSharePointSearch).originalQuery}
              </span>
            </div>
          )}
          <div style={contentAreaStyle}>{renderExpandedContent()}</div>
        </div>
      </div>
    </div>
  );
};

export default SharePointSearchSection;
